<script setup>
// Page 9 — FAQ: native disclosure, no dependency.
//
// Seven questions, and every one of them is an objection that blocks a purchase:
// where the money lands, what happens when a debit fails, how an owner consents,
// whether a beheerder can run several VvEs, what Pandig does NOT replace, what the
// € 1 excludes, and how a decision of the vergadering reaches the collection. The
// version this replaced asked "Voor wie is Pandig?" and answered it with "elke
// organisatie" — a question nobody was blocked on, answered in a way that undid the
// page's own positioning.
//
// Answers stay inside what the code actually does. In particular: a failed debit is
// answered with visibility and with who decides what happens next, because there is no
// automatic retry and no notification email in the product today — and a promise about
// someone's money is the last place to get ahead of the build.
//
// Scroll-scrubbed over a taller sticky section (the same shape as LandingStakes,
// FeatureDeck, LandingHow, LandingPricing, and LandingFlows), not a
// once-triggered reveal on a single-viewport .snap-page. Two problems that
// fixes at once: a once:true stagger could be scrolled past before it played,
// and — the actual "jutters back" bug — opening a <details> answer grows that
// item's height; on a real CSS scroll-snap page that layout shift nudges the
// browser into re-snapping mid-read. A section whose own height comes from a
// fixed inline style (not from its content) can't be perturbed that way, so
// there's nothing left for scroll-snap to react to — it's opted out of
// scroll-snap entirely, same as the other tall pages.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronDown } from "@lucide/vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, SCRUB, DIM } from "@/lib/motion";

const { t } = useI18n();

const faqs = [1, 2, 3, 4, 5, 6, 7].map((n) => ({ q: `landing.faq.q${n}`, a: `landing.faq.a${n}` }));

const root = ref(null);
const faqHeading = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    const targets = [faqHeading.value, ...root.value.querySelectorAll(".faq-item")];
    gsap.set(targets, { opacity: DIM, y: 24 });

    const tl = gsap.timeline({ paused: true }).to(targets, { opacity: 1, y: 0, duration: 0.55, stagger: 0.06 });

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Its content fades in across the section's own scroll range, so jumping to
       the section's top edge would land on a blank panel: a menu/pager jump goes
       to the settled end instead (see scrollElementIntoView in lib/scroll.js). -->
  <section
    id="vragen"
    ref="root"
    class="relative"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[130vh] sm:h-[150vh]'"
    data-jump="settled"
    :data-page-label="t('landing.pager.faq')"
  >
    <!-- overflow-y-auto (not overflow-hidden): opening a <details> answer can grow
         this content past one viewport on a real phone, and it needs to stay
         reachable (by scrolling within the pinned camera) rather than being
         centred-and-clipped out of view. [align-items:safe_center] centres it like every
         other tall page when it fits, and only falls back to start-alignment when
         it doesn't — so nothing ends up pushed above the reachable scroll range. -->
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-visible'">
      <!-- Seven collapsed rows have to fit the same phone screen four used to, so the
           question text steps down a size and the rows lose a third of their padding
           below sm: — the answers are hidden until asked for, so what has to fit is
           seven summaries, not seven paragraphs. -->
      <div class="mx-auto w-full max-w-3xl px-5 py-6 sm:py-14">
        <h2 ref="faqHeading" class="font-sans text-xl font-bold tracking-tight text-foreground sm:text-3xl">{{ t("landing.faq.title") }}</h2>
        <div class="mt-3 border-t-2 border-foreground sm:mt-8">
          <details v-for="faq in faqs" :key="faq.q" class="faq-item group border-b-2 border-foreground">
            <summary class="flex cursor-pointer list-none items-center justify-between gap-3 py-2.5 font-sans text-sm font-semibold leading-snug text-foreground sm:gap-4 sm:py-5 sm:text-base">
              {{ t(faq.q) }}
              <ChevronDown class="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180 sm:size-5" />
            </summary>
            <p class="pb-3 text-xs leading-relaxed text-muted-foreground sm:pb-5 sm:text-sm">{{ t(faq.a) }}</p>
          </details>
        </div>
      </div>
    </div>
  </section>
</template>
